// The Booking reminders page's pure half (spec 055 D16–D25). The limits and the sentences match
// the app repo's functions/bookingReminderRules.js, which has the last word on every save.

export const LIMITS = { steps: 10, gap: 365, title: 60, body: 200 };

export const GROUPS = [
  {
    key: "hadLessons",
    label: "Had lessons",
    help: "Nothing booked ahead. Counted from their last lesson.",
    firstUnit: "days without a booking",
    messages: [
      { key: "hadLessonsCredits", label: "Has credits" },
      { key: "hadLessonsNoCredits", label: "No credits" },
    ],
  },
  {
    key: "neverBooked",
    label: "Never booked",
    help: "Saved their profile, never booked. Counted from sign-up.",
    firstUnit: "days since sign-up",
    messages: [
      { key: "neverBookedTrial", label: "Free trial" },
      { key: "neverBookedNoTrial", label: "No free trial" },
    ],
  },
  {
    key: "notFinished",
    label: "Didn't finish sign-up",
    help: "Chose student, never saved the profile. A push only, no card. Counted from sign-up.",
    firstUnit: "days since sign-up",
    messages: [
      { key: "notFinishedTrial", label: "Free trial" },
      { key: "notFinishedNoTrial", label: "No free trial" },
    ],
  },
];

export const MESSAGE_LABELS = Object.fromEntries(
  GROUPS.flatMap((g) => g.messages.map((m) => [m.key, `${g.label}, ${m.label.toLowerCase()}`]))
);

export const HOURS = Array.from({ length: 24 }, (_, h) => h);
export const hourLabel = (h) => `${String(h).padStart(2, "0")}:00`;

const ORDINALS = ["1st", "2nd", "3rd"];
export const ordinal = (n) => ORDINALS[n - 1] || `${n}th`;

// A number field's value: empty is NaN, which fails the checks below instead of reading as 0.
export const toNumber = (value) => (value === "" ? NaN : Number(value));

const isWhole = (n, min, max) => Number.isInteger(n) && n >= min && n <= max;

// "Days 7, 10, 20, 30, then every 30": the day each reminder goes, counted from the start.
export const dayList = (steps, thenEvery) => {
  if (steps.length === 0 || !steps.every((n) => isWhole(n, 1, LIMITS.gap)) || !isWhole(thenEvery, 0, LIMITS.gap)) return "";
  let day = 0;
  const days = steps.map((gap) => (day += gap));
  return `Days ${days.join(", ")}, ${thenEvery > 0 ? `then every ${thenEvery}` : "then stop"}`;
};

const PLACEHOLDERS = ["name", "days", "credits"];
export const placeholderProblem = (text) => {
  for (const [, name] of String(text).matchAll(/\{([^}]*)\}/g)) {
    if (!PLACEHOLDERS.includes(name)) return `{${name}} is not a placeholder. Use {name}, {days} or {credits}.`;
  }
  return null;
};

export const messageProblem = (label, m) => {
  const title = (m?.title || "").trim();
  const body = (m?.body || "").trim();
  if (title.length < 1 || title.length > LIMITS.title) return `${label}: the title must be 1 to ${LIMITS.title} characters.`;
  if (body.length < 1 || body.length > LIMITS.body) return `${label}: the text must be 1 to ${LIMITS.body} characters.`;
  const problem = placeholderProblem(title) || placeholderProblem(body);
  return problem ? `${label}: ${problem}` : null;
};

// The first thing wrong with the form, in the server's words and order — or null.
export const firstProblem = (s) => {
  if (!isWhole(s.hour, 0, 23)) return "The send time must be a whole hour from 00:00 to 23:00.";
  for (const g of GROUPS) {
    const v = s.groups[g.key];
    if (v.steps.length < 1 || v.steps.length > LIMITS.steps) return `${g.label}: have 1 to ${LIMITS.steps} reminders.`;
    if (!v.steps.every((n) => isWhole(n, 1, LIMITS.gap))) return `${g.label}: each reminder must be 1 to ${LIMITS.gap} days.`;
    if (!isWhole(v.thenEvery, 0, LIMITS.gap)) return `${g.label}: "Then every" must be 0 to ${LIMITS.gap} days.`;
  }
  for (const g of GROUPS) {
    for (const m of g.messages) {
      const problem = messageProblem(MESSAGE_LABELS[m.key], s.messages[m.key]);
      if (problem) return problem;
    }
  }
  return null;
};

// The job's skip reasons (functions/bookingReminders.js), in words.
export const SKIP_LABELS = {
  booked: "Has a lesson booked",
  notDue: "Not due yet",
  noDevice: "No device",
  switchedOff: "Turned reminders off",
  groupOff: "Group switched off",
  disabled: "Disabled account",
  internal: "Team test account",
  noSummary: "No lesson summary",
  noCreatedAt: "No sign-up date",
  failed: "Check or send failed",
};

// The Last run card (D25).
export const lastRunView = (run) => {
  if (!run) return null;
  const when = run.at
    ? new Date(run.at).toLocaleString("en-GB", { timeZone: "Europe/London", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
    : "—";
  const counts = run.counts || {};
  const rows = GROUPS.flatMap((g) => g.messages.map((m) => ({ key: m.key, label: MESSAGE_LABELS[m.key], count: counts[m.key] || 0 })));
  return {
    heading: `${when} UK time · ${run.live ? "Sent" : "Count only"}`,
    countLabel: run.live ? "Sent" : "Would get",
    rows,
    total: rows.reduce((sum, r) => sum + r.count, 0),
    skips: Object.entries(run.skipped || {})
      .filter(([, n]) => n > 0)
      .map(([reason, n]) => ({ reason, label: SKIP_LABELS[reason] || reason, count: n }))
      .sort((a, b) => b.count - a.count),
  };
};
